import React, { useState, useRef, useEffect } from 'react';
import {
  Users,
  FileText,
  UserCheck,
  CircleDollarSign,
  MoreHorizontal,
  FileCheck,
  DollarSign,
  X,
  Wrench,
  LayoutDashboard
} from 'lucide-react';
import { playPulseSeal } from '../utils/pulseAudio';
import { playOwnerBloomNavSound } from '../utils/bloomOpenSound';
import { UserSession, UserRole } from '../types';
import { MobileAccountRoleSection } from './MobileAccountRoleSection';

interface MobileOwnerBottomNavProps {
  currentTab: string;
  onSelectTab: (tab: string) => void;
  onOpenUniversalTools?: () => void;
  user?: UserSession;
  onSwitchRole?: (role: UserRole) => void;
  onLogout?: () => void;
}

interface NavDestination {
  id: string;
  label: string;
  tab: string;
  icon: React.ComponentType<{ className?: string; strokeWidth?: number; size?: number }>;
  isActive: (current: string) => boolean;
  activeColor: {
    text: string;
    icon: string;
    indicator: string;
    glow: string;
  };
}

export const MobileOwnerBottomNav: React.FC<MobileOwnerBottomNavProps> = ({
  currentTab,
  onSelectTab,
  onOpenUniversalTools,
  user,
  onSwitchRole,
  onLogout
}) => {
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const moreSheetRef = useRef<HTMLDivElement>(null);

  // Close more menu when clicking outside
  useEffect(() => {
    const handleTouchOutside = (e: MouseEvent | TouchEvent) => {
      if (moreSheetRef.current && !moreSheetRef.current.contains(e.target as Node)) {
        setIsMoreOpen(false);
      }
    };
    if (isMoreOpen) {
      document.addEventListener('mousedown', handleTouchOutside);
      document.addEventListener('touchstart', handleTouchOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleTouchOutside);
      document.removeEventListener('touchstart', handleTouchOutside);
    };
  }, [isMoreOpen]);

  const destinations: NavDestination[] = [
    {
      id: 'owner-nav-charts',
      label: 'Charts',
      tab: 'clients',
      icon: Users,
      isActive: (cur) => cur === 'clients',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'owner-nav-documents',
      label: 'Documents',
      tab: 'clinical',
      icon: FileText,
      isActive: (cur) => cur === 'clinical',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'owner-nav-providers',
      label: 'Providers',
      tab: 'supervision',
      icon: UserCheck,
      isActive: (cur) => cur === 'supervision',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'owner-nav-billing',
      label: 'Billing',
      tab: 'billing',
      icon: CircleDollarSign,
      isActive: (cur) => cur === 'billing',
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    },
    {
      id: 'owner-nav-more',
      label: 'More',
      tab: 'more',
      icon: MoreHorizontal,
      isActive: (cur) => cur === 'claims' || cur === 'payroll' || cur === 'dashboard' || isMoreOpen,
      activeColor: {
        text: 'text-slate-900',
        icon: 'text-slate-900',
        indicator: 'bg-slate-900',
        glow: 'shadow-none'
      }
    }
  ];

  const handleTap = (tab: string) => {
    playOwnerBloomNavSound(user);
    if (tab === 'more') {
      setIsMoreOpen(!isMoreOpen);
    } else {
      setIsMoreOpen(false);
      onSelectTab(tab);
    }
  };

  const handleSelectMoreOption = (tab: string) => {
    playOwnerBloomNavSound(user);
    setIsMoreOpen(false);
    onSelectTab(tab);
  };

  return (
    <>
      {/* More Action Sheet Modal (Tactile Popover above Bottom Nav) */}
      {isMoreOpen && (
        <div className="fixed inset-0 z-40 bg-black/40 backdrop-blur-xs md:hidden animate-in fade-in duration-150">
          <div
            ref={moreSheetRef}
            className="absolute bottom-[calc(52px+env(safe-area-inset-bottom,0px))] inset-x-3 bg-white rounded-2xl border border-slate-200 p-3 space-y-2 animate-in slide-in-from-bottom-3 duration-200 shadow-xl"
          >
            <div className="flex items-center justify-between pb-2 border-b border-slate-100 px-1">
              <span className="text-[11px] font-mono font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1.5">
                <span>Owner Operations • More</span>
              </span>
              <button
                type="button"
                onClick={() => setIsMoreOpen(false)}
                className="p-1 rounded-lg text-slate-400 hover:text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors cursor-pointer"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>

            <div className="grid grid-cols-1 gap-2 pt-1">
              {/* Dashboard Option */}
              <button
                type="button"
                id="owner-more-dashboard"
                onClick={() => handleSelectMoreOption('dashboard')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'dashboard'
                    ? 'bg-slate-100 border border-slate-300 text-slate-950 font-semibold shadow-xs'
                    : 'bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                    <LayoutDashboard className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Dashboard</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200">
                        Executive
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Practice overview, agency metrics & activity
                    </div>
                  </div>
                </div>
              </button>

              {/* Universal Tools Option */}
              <button
                type="button"
                id="owner-more-universal-tools"
                onClick={() => {
                  playPulseSeal({ cooldownMs: 120, volume: 0.35 });
                  setIsMoreOpen(false);
                  if (onOpenUniversalTools) {
                    onOpenUniversalTools();
                  } else {
                    window.dispatchEvent(new CustomEvent('pulse_open_mobile_universal_tools'));
                  }
                }}
                className="w-full flex items-center justify-between p-3 rounded-xl border border-slate-200 hover:border-slate-300 bg-white hover:bg-slate-50 text-slate-800 shadow-xs transition-all text-left cursor-pointer active:scale-[0.99]"
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                    <Wrench className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Universal Tools</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200 font-semibold">
                        Suite
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Call, Fax, Email / Draft, Add Note, Clinical Doc, Sound
                    </div>
                  </div>
                </div>
              </button>

              {/* Claims & 837 EDI Option */}
              <button
                type="button"
                id="owner-more-claims"
                onClick={() => handleSelectMoreOption('claims')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'claims'
                    ? 'bg-slate-100 border border-slate-300 text-slate-950 font-semibold shadow-xs'
                    : 'bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                    <FileCheck className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Claims & 837 EDI</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200">
                        837 Batching
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Batch generation & electronic transmissions
                    </div>
                  </div>
                </div>
              </button>

              {/* Payroll Option */}
              <button
                type="button"
                id="owner-more-payroll"
                onClick={() => handleSelectMoreOption('payroll')}
                className={`w-full flex items-center justify-between p-3 rounded-xl transition-all text-left cursor-pointer active:scale-[0.99] ${
                  currentTab === 'payroll'
                    ? 'bg-slate-100 border border-slate-300 text-slate-950 font-semibold shadow-xs'
                    : 'bg-white hover:bg-slate-50 border border-slate-200 text-slate-800 shadow-xs'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <div className="w-9 h-9 rounded-lg bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-700">
                    <DollarSign className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-slate-900 flex items-center gap-1.5">
                      <span>Payroll</span>
                      <span className="text-[9px] font-mono px-1.5 py-0.2 rounded bg-slate-100 text-slate-700 border border-slate-200">
                        Compensation
                      </span>
                    </div>
                    <div className="text-[10.5px] text-slate-500">
                      Provider compensation & payroll records
                    </div>
                  </div>
                </div>
              </button>
            </div>

            {/* Account & Role Section (Includes Role Switcher & Sign Out) */}
            {user && (
              <MobileAccountRoleSection
                user={user}
                onSwitchRole={onSwitchRole}
                onLogout={onLogout}
                onAfterAction={() => setIsMoreOpen(false)}
              />
            )}
          </div>
        </div>
      )}

      {/* Owner Fixed Bottom Navigation */}
      <nav
        id="pulse-mobile-owner-bottom-nav"
        aria-label="Owner Mobile Navigation"
        className="fixed bottom-0 left-0 right-0 z-40 md:hidden bg-white/95 border-t border-slate-200/90 shadow-[0_-2px_10px_rgba(15,23,42,0.06)] select-none backdrop-blur-md"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* 5 Primary Destination Grid */}
        <div className="grid grid-cols-5 h-[52px] items-stretch">
          {destinations.map((item) => {
            const active = item.isActive(currentTab);
            const Icon = item.icon;

            return (
              <button
                key={item.id}
                id={item.id}
                type="button"
                onClick={() => handleTap(item.tab)}
                className="relative flex flex-col items-center justify-center h-full py-1 px-1 transition-all duration-150 active:scale-90 group focus:outline-none cursor-pointer"
                aria-current={active ? 'page' : undefined}
              >
                {/* Active top edge indicator bar */}
                {active && (
                  <div
                    className={`absolute top-0 h-[2.5px] w-8 rounded-full ${item.activeColor.indicator} transition-all duration-200`}
                  />
                )}

                {/* Icon */}
                <div className="relative flex items-center justify-center shrink-0">
                  <Icon
                    className={`w-5 h-5 transition-all duration-150 ${
                      active
                        ? `${item.activeColor.icon} scale-105`
                        : 'text-slate-500 group-hover:text-slate-700'
                    }`}
                    strokeWidth={active ? 2.3 : 1.9}
                  />
                </div>

                {/* Destination Label - Single line guaranteed */}
                <span
                  className={`text-[9.5px] tracking-tight leading-none mt-1 whitespace-nowrap transition-colors select-none ${
                    active
                      ? `font-bold ${item.activeColor.text}`
                      : 'font-medium text-slate-500 group-hover:text-slate-700'
                  }`}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>
  );
};
